import { createSelector } from 'reselect';
import { getFormValues } from 'redux-form';
import { addDays, endOfDay, startOfDay } from 'date-fns';

import { RootState } from '../../reducers';
import { Preset } from '../../../core/api/preset/preset.types';
import { Forms } from '../../form/form.selector';
import { ReportsPageFormValues } from '../../../routes/Reports/ReportsPage/ReportsListPage/ReportsListPage';
import { DateRangeDefined } from '../dataTools/dataTools.types';
import { PresetDateRanges, PresetKind } from '../../../config/constants';
import { getStartEndDates } from '../../../core/helpers/utils/dateUtils';
import { getDaysBack } from '../../../shared/DateRangePicker/utils';
import { SortingOrder } from '../reports/reports.types';

export const selectPreset = (state: RootState): Preset[] => state.entities.presets || [];

export const getPresetListFiltered = createSelector([selectPreset], (presets: Preset[]) =>
  presets.filter((preset) => preset.kind !== PresetKind.DEFAULT),
);

export const getSelectedReport = createSelector(
  [getFormValues(Forms.reportsPageForm) as any],
  (reportsFormValues: ReportsPageFormValues): string => {
    return reportsFormValues ? reportsFormValues.selectedType : '';
  },
);

export const getFeedingListPreset = createSelector([selectPreset], (presets: Preset[]) => {
  const preset = presets.find((preset) => preset.reportType === 'feedingList');

  const dataSettings = preset?.dataSettings ? { ...preset.dataSettings } : {};
  // default dataSettings is empty object. set default values for form

  if (!dataSettings.sortingOrder) {
    dataSettings.sortingOrder = SortingOrder.ASCENDING;
  }
  if (!dataSettings.sortingColumn) {
    dataSettings.sortingColumn = 'description';
  }

  if (preset?.viewSettings?.pageOrientation !== undefined) {
    const { pageOrientation, ...rest } = preset.viewSettings;
    return { ...preset, dataSettings, viewSettings: rest, name: preset.name ? preset.name : 'Feeding list preset' };
  }

  return { ...preset, viewSettings: preset?.viewSettings, dataSettings };
});

export const getRefusalsPreset = createSelector([selectPreset], (presets: Preset[]): any => {
  const preset = presets.find((preset) => preset.reportType === 'refusals');

  const dataSettings = preset?.dataSettings ? { ...preset.dataSettings } : {};
  const viewSettings = preset?.viewSettings ? { ...preset.viewSettings } : {};

  if (!dataSettings.daysBack) {
    dataSettings.daysBack = 14;
  }
  if (viewSettings.collapsed === undefined) {
    viewSettings.collapsed = 'true';
  } else {
    viewSettings.collapsed = viewSettings.collapsed.toString();
  }

  return { ...preset, viewSettings, dataSettings };
});

export const getStockTransactionPreset = createSelector([selectPreset], (presets: Preset[]): Preset => {
  const preset = presets.find((preset) => preset.reportType === 'StockTransaction')!;
  const dataSettings = preset?.dataSettings ? { ...preset.dataSettings } : {};
  const viewSettings = preset?.viewSettings ? { ...preset.viewSettings } : {};
  dataSettings.daysBack = getDaysBack(dataSettings.dateRangeType as PresetDateRanges, dataSettings.daysBack || 0) || 0;
  if (viewSettings.collapsed === undefined) {
    viewSettings.collapsed = 'true';
  } else {
    viewSettings.collapsed = viewSettings.collapsed.toString();
  }
  return { ...preset, viewSettings, dataSettings };
});

export const getRefusalsToolbarInitialValues = createSelector(
  [getRefusalsPreset],
  (preset: Preset): { initialValues: { selectedTimeRange: DateRangeDefined } } => {
    const daysBAck = preset.dataSettings.daysBack;
    return {
      initialValues: {
        selectedTimeRange: {
          startDate: startOfDay(addDays(new Date(), daysBAck ? 1 - daysBAck : -6)),
          endDate: endOfDay(new Date()),
        },
      },
    };
  },
);

export const getStockTransactionToolbarInitialValues = createSelector([getStockTransactionPreset], (preset: Preset) => {
  const daysBack = preset.dataSettings.daysBack;
  const endDate = preset.dataSettings.endDate;
  const dateRangeType = preset.dataSettings.dateRangeType as PresetDateRanges;
  const startEndDates = getStartEndDates(new Date(endDate!), daysBack, dateRangeType);

  return {
    selectedTimeRange: {
      startDate: startEndDates.startDate,
      endDate: startEndDates.endDate,
    },
  };
});

export const getSortingOrder = createSelector([getFeedingListPreset], (preset) => preset.dataSettings.sortingOrder);
